import StaticPage from "@/components/StaticPage";

export default function Privacy() {
  return (
    <StaticPage eyebrow="Legal" title="Privacy Policy" updatedAt="July 2026">
      <p className="text-[#4A4A4A]">
        NextVisit is a service operated by Brooke Tomlin doing business as NextVisit (&quot;NextVisit,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), a sole proprietorship.
        We take privacy seriously and only collect the data we need to run the product. This policy explains what we collect, why, and what your rights are.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">1. Who this policy applies to</h2>
      <p>
        Two groups of people are covered here:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>NextVisit customers</b> — the businesses (and their team members) who sign up for a trial or paid plan.</li>
        <li><b>End customers</b> — the appointment clients of those businesses who receive follow-up messages sent through NextVisit.</li>
      </ul>
      <p>
        For end customers, the business you booked with is the <b>data controller</b> and NextVisit is a <b>data processor</b>. Requests to access, correct, or delete end-customer data should be directed to the business you have an appointment with; we&apos;ll help them fulfill your request.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">2. What we collect</h2>
      <p><b>From NextVisit customers we collect:</b></p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Business name, contact name, email, phone (optional), business type.</li>
        <li>Password (stored only as a bcrypt hash — we never see or store the plaintext).</li>
        <li>Payment information via Stripe. Card numbers never touch our servers.</li>
        <li>Product usage: which pages you visit inside Studio, which templates you edit, which integrations you connect.</li>
        <li>Content you configure: your business name, sender name, booking link, review link, message templates.</li>
      </ul>
      <p><b>From end customers of our customers, we process:</b></p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Name, email address, phone (optional), notes provided by the business.</li>
        <li>SMS consent status (whether the business recorded opt-in for text follow-ups), and opt-out events (e.g. STOP replies).</li>
        <li>Appointment history: service, time, source (manual entry or Square sync).</li>
        <li>Message delivery status (sent, opened via optional Resend metadata, unsubscribed; for SMS, Twilio delivery status when enabled).</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">3. How we use it</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>To deliver the NextVisit service — sending scheduled follow-up messages (email and, when enabled and consented, SMS), syncing with your booking system, drafting templates with AI when you request it.</li>
        <li>To communicate with you: welcome emails, product updates, security alerts, invoices.</li>
        <li>To keep the service secure: rate limiting, brute-force protection, fraud prevention.</li>
        <li>To improve the product using aggregate, anonymized usage data.</li>
      </ul>
      <p>
        We do <b>not</b> sell your data or your customers&apos; data. We do not run ad networks or share personal data with data brokers. We do not train our AI provider&apos;s models on your data (see subprocessors below).
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">4. SMS / text messaging</h2>
      <p>
        When a business enables SMS for a customer, NextVisit may send appointment follow-up texts (thank-you, review request, rebook, win-back) to the phone number on file.
        We only send SMS when the business has recorded prior consent in NextVisit. Consent is not buried in this Privacy Policy — it is collected via a separate opt-in control in Studio (or CSV import attestation). See{" "}
        <a className="text-[#183D36] underline" href="/messaging-consent">Messaging consent</a> for the exact language and sample messages.
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Msg &amp; data rates may apply.</li>
        <li>Reply <b>STOP</b> to opt out; <b>START</b> to resubscribe; <b>HELP</b> for help.</li>
        <li>Opt-outs are stored so we do not re-message that number through NextVisit.</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">5. Subprocessors</h2>
      <p>
        We use a small set of trusted vendors to operate the service. Each is bound by a data processing agreement.
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>MongoDB Atlas</b> — encrypted database hosting.</li>
        <li><b>Resend</b> — transactional email delivery.</li>
        <li><b>Twilio</b> — SMS delivery and inbound STOP/HELP handling (when SMS is enabled).</li>
        <li><b>Stripe</b> — payments and subscription billing.</li>
        <li><b>Square</b> — appointment sync (only if you choose to connect it).</li>
        <li><b>Anthropic (via Emergent LLM key)</b> — AI-drafted templates. Requests are sent per session; Anthropic does not train models on API traffic.</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">6. Data retention</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Active accounts:</b> we retain data for as long as the account is active.</li>
        <li><b>Canceled accounts:</b> customer + appointment data is deleted within 90 days of cancellation. Billing records are retained for 7 years for tax compliance.</li>
        <li><b>Unsubscribed end customers:</b> we retain the record indefinitely so we never accidentally re-send to them.</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">7. Your rights</h2>
      <p>
        Depending on where you live (GDPR, CCPA, and similar), you have the right to access, correct, port, or delete the personal data we hold about you, and to object to certain kinds of processing. Email <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a> with your request and we&apos;ll respond within 30 days.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">8. Data Processing Agreement (DPA)</h2>
      <p>
        Enterprise customers or EU-based businesses that need a signed DPA can request one by emailing <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a>. We&apos;ll countersign and return within one business day.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">9. Security</h2>
      <p>
        See our <a className="text-[#183D36] underline" href="/security">Security page</a> for details on encryption, authentication, and incident response.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">10. Changes to this policy</h2>
      <p>
        If we make material changes, we&apos;ll email active account owners at least 30 days before the change takes effect.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">11. Contact</h2>
      <p>
        Questions? Email <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a> or use the <a className="text-[#183D36] underline" href="/contact">contact form</a>.
      </p>
    </StaticPage>
  );
}
